import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue, useSetAtom } from 'jotai';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { SxProps, Theme } from '@mui/material/styles';
import QrCodeScannerOutlinedIcon from '@mui/icons-material/QrCodeScannerOutlined';

import { userPermissionsAtom } from 'atoms/permissions';
import { hasPermissions } from 'utils/permissions';
import { isWorkCardScannerOpenAtom } from './workCardAtoms';

interface WorkCardScanButtonProps {
  /** `header`: filled white button on the purple app bar; `icon`: compact icon button for small screens. */
  variant?: 'header' | 'icon';
  /** Extra styles, applied to the `icon` variant only (used for positioning in the app bar). */
  sx?: SxProps<Theme>;
}

/**
 * Opens the work-card QR scanner from the app header. Rendered only for users with ERGANI access.
 */
const WorkCardScanButton: React.FC<WorkCardScanButtonProps> = ({ variant = 'header', sx }) => {
  const { t } = useTranslation();
  const permissions = useAtomValue(userPermissionsAtom);
  const openScanner = useSetAtom(isWorkCardScannerOpenAtom);

  if (!hasPermissions(permissions, ['ergani:access'])) {
    return null;
  }

  const handleOpenScanner = () => openScanner(true);

  if (variant === 'icon') {
    return (
      <Tooltip title={t('workCard.headerButton')}>
        <IconButton color="inherit" aria-label={t('workCard.headerButton')} onClick={handleOpenScanner} sx={sx}>
          <QrCodeScannerOutlinedIcon />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Button
      variant="contained"
      disableElevation
      startIcon={<QrCodeScannerOutlinedIcon />}
      onClick={handleOpenScanner}
      sx={{
        color: 'primary.main',
        backgroundColor: '#fff',
        borderRadius: '4px',
        textTransform: 'none',
        whiteSpace: 'nowrap',
        boxShadow: 'none',
        '&:hover': { backgroundColor: '#F3F5F7', boxShadow: 'none' },
      }}
    >
      {t('workCard.headerButton')}
    </Button>
  );
};

export default WorkCardScanButton;
